import { apiEmployeeAdditionalCompensation } from "@/api/employees/employee-additional-compensation.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiEmployeeAdditionalCompensationSave } from "@/types/employees/employee-additional-compensation.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const employeeAdditionalCompensationsKeys = createQueryKeys(
  "employeeAdditionalCompensations",
  {
    all: (employeeId: number) => ({
      queryKey: [employeeId],
      queryFn: async () => {
        const result = await apiEmployeeAdditionalCompensation.getAll(employeeId);
        if (result.error) throw new ApiErrorException(result.error);
        return result.data;
      },
    }),
  }
);

export const useEmployeeAdditionalCompensations = (employeeId: number, enabled: boolean = true) => {
  return useQuery({
    ...employeeAdditionalCompensationsKeys.all(employeeId),
    enabled: enabled,
  });
};

export const useEmployeeAdditionalCompensationSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      employeeId,
      employeeAdditionalCompensationId,
      payload,
    }: {
      employeeId: number;
      employeeAdditionalCompensationId: number | undefined;
      payload: ApiEmployeeAdditionalCompensationSave;
    }) => {
      const result = !employeeAdditionalCompensationId
        ? await apiEmployeeAdditionalCompensation.create(employeeId, payload)
        : await apiEmployeeAdditionalCompensation.update(
            employeeId,
            employeeAdditionalCompensationId,
            payload
          );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: employeeAdditionalCompensationsKeys.all._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmployeeAdditionalCompensationDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      employeeId,
      employeeAdditionalCompensationId,
    }: {
      employeeId: number;
      employeeAdditionalCompensationId: number;
    }) => {
      const result = await apiEmployeeAdditionalCompensation.delete(
        employeeId,
        employeeAdditionalCompensationId
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: employeeAdditionalCompensationsKeys.all._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
